import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  addToWishlistAPI,
  removeFromWishlistAPI,
  getWishlistAPI,
  getUserNotificationsAPI,
  userUpdateAPI,
  getDiscountsAPI,
  markNotificationAsReadAPI,
  markAllNotificationsAsReadAPI
} from "../../actions";
import NotificationList from "./NotificationList";
import Card from "../Shared/Card";
import "./Dashboard.css";

function Empty({ icon, title, body }) {
  return (
    <div className="dashboard-empty">
      <div className="dashboard-empty-icon">{icon}</div>
      <div className="dashboard-empty-title">{title}</div>
      <div className="dashboard-empty-body">{body}</div>
    </div>
  );
}

function UserDashboard({
  user,
  wishlist,
  notifications,
  organizer,
  loading,
  token,
  discounts,
  addToWishlist,
  removeFromWishlist,
  getWishlist,
  getUserNotifications,
  getDiscounts,
  updateUser,
  markNotificationAsRead,
  markAllNotificationsAsRead,
}) {
  const isOrganizer = !!(user?.is_organizer || organizer);

  const [tab, setTab] = useState("saved");
  const [greeting, setGreeting] = useState("Welcome");
  const [savedIds, setSavedIds] = useState(new Set());
  const [loadingWishlist, setLoadingWishlist] = useState(false);
  const [wishlistError, setWishlistError] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterRead, setFilterRead] = useState("unread"); // "all" | "unread" | "read"

  // Use refs to track if we've already fetched to avoid infinite loops
  const hasFetchedWishlist = useRef(false);
  const hasFetchedDiscounts = useRef(false);

  const [uName, setUName] = useState("");
  const [uEmail, setUEmail] = useState("");
  const [uPhone, setUPhone] = useState("");
  const [preview, setPreview] = useState(null);

  // Sync state with user prop
  useEffect(() => {
    if (user) {
      setUName(user.name || "");
      setUEmail(user.email || "");
      setUPhone(user.contact || "");
      setPreview(user.profile_pic || null);
    }
  }, [user]);
  const [newFile, setNewFile] = useState(null);
  const [emailErr, setEmailErr] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const [saveState, setSave] = useState(null);
  const fileRef = useRef();

  const unread = notifications?.count || 0;

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(
      h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening",
    );
  }, []);

  // Fetch wishlist and notifications on mount
  useEffect(() => {
    // Fetch wishlist from API to get fresh data (only once)
    if (!hasFetchedWishlist.current) {
      hasFetchedWishlist.current = true;

      const shouldFetchWishlist = !wishlist ||
        (Array.isArray(wishlist) && wishlist.length === 0) ||
        (wishlist && !wishlist.results);

      if (shouldFetchWishlist) {
        setLoadingWishlist(true);
        setWishlistError(null);

        const result = getWishlist();
        // Handle case where action might not return a promise
        if (result && typeof result.then === 'function') {
          result
            .then(() => setLoadingWishlist(false))
            .catch((err) => {
              console.error("Error fetching wishlist:", err);
              setWishlistError("Failed to load saved discounts");
              setLoadingWishlist(false);
            });
        } else {
          // If action doesn't return promise, assume it handled the dispatch internally
          setLoadingWishlist(false);
        }
      }
    }

    // Fetch all discounts if not already loaded (only once)
    if (!hasFetchedDiscounts.current) {
      hasFetchedDiscounts.current = true;
      if (!discounts || (discounts.results && discounts.results.length === 0)) {
        getDiscounts();
      }
    }

  }, []); // Empty deps - only run on mount

  // Re-fetch notifications when filters change
  useEffect(() => {
    const filters = {};
    if (filterType) filters.type = filterType;
    if (filterRead === "unread") filters.is_read = false;
    else if (filterRead === "read") filters.is_read = true;
    getUserNotifications(filters);
  }, [filterType, filterRead, getUserNotifications]);

  // Transform wishlist API response to extract discount data
  // The API returns an array like: [{ id: 1, discount: "https://.../discounts/53/" }, ...]
  // We need to extract the discount ID from the URL and match with full discount data from store
  const transformWishlist = (wishlistData, allDiscounts) => {
    if (!wishlistData) return [];

    // Helper to get discount ID from URL
    const getDiscountIdFromUrl = (url) => {
      if (!url) return null;
      const match = url.match(/\/discounts\/(\d+)\/?$/);
      return match ? parseInt(match[1]) : null;
    };

    // Get all discount IDs from store
    let discountIdMap = {};
    if (allDiscounts && allDiscounts.results && Array.isArray(allDiscounts.results)) {
      allDiscounts.results.forEach(d => {
        discountIdMap[d.id] = d;
      });
    } else if (Array.isArray(allDiscounts)) {
      allDiscounts.forEach(d => {
        discountIdMap[d.id] = d;
      });
    }

    // Check if it's in paginated format (count/results) - older format
    if (wishlistData.results && Array.isArray(wishlistData.results)) {
      return wishlistData.results
        .filter(item => item.discount) // Filter out any items without discount data
        .map(item => ({
          ...item.discount, // Spread the discount properties to top level
          _wishlistId: item.id, // Keep track of original wishlist item id
          _savedAt: item.created_at
        }));
    }

    // If it's an array (newer format from API), extract discount IDs from URLs and match with full data
    if (Array.isArray(wishlistData)) {
      // First, filter out duplicates by using a Set to track seen discount IDs
      const seenIds = new Set();
      const uniqueItems = wishlistData.filter(item => {
        if (!item.discount) return false;
        const discountId = getDiscountIdFromUrl(item.discount);
        if (seenIds.has(discountId)) return false;
        seenIds.add(discountId);
        return true;
      });

      return uniqueItems
        .map(item => {
          // Extract discount ID from URL like "https://.../discounts/53/"
          const discountId = getDiscountIdFromUrl(item.discount);

          // Get full discount data from store if available
          const fullDiscount = discountId ? discountIdMap[discountId] : null;

          return {
            ...fullDiscount, // Spread full discount properties (id, title, flyer, etc.)
            id: discountId, // Ensure ID is set
            _wishlistId: item.id, // Keep track of original wishlist item id
            _savedAt: item.created_at
          };
        });
    }

    return [];
  };

  // Get transformed saved items (pass discounts to match with wishlist)
  const savedItems = transformWishlist(wishlist, discounts);

  // Sync saved IDs with wishlist - only update when savedItems actually changes
  useEffect(() => {
    if (savedItems.length > 0) {
      const newIds = new Set(savedItems.map((w) => w.id));
      // Only update if the sets are different
      if (newIds.size !== savedIds.size || [...newIds].some(id => !savedIds.has(id))) {
        setSavedIds(newIds);
      }
    }
  }, [savedItems, savedIds, setSavedIds]);

  const toggleSave = (id) => {
    const isCurrentlySaved = savedIds.has(id);

    // Optimistic update
    setSavedIds((p) => {
      const n = new Set(p);
      if (isCurrentlySaved) {
        n.delete(id);
        // Call API to remove from wishlist
        removeFromWishlist({ discount_id: id });
      } else {
        n.add(id);
        // Call API to add to wishlist
        addToWishlist({ discount_id: id });
      }
      return n;
    });
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setNewFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (emailErr || phoneErr) return;
    setSave("saving");

    const fd = new FormData();
    fd.append("name", uName);
    fd.append("email", uEmail);
    fd.append("contact", uPhone);
    if (newFile) fd.append("profile_pic", newFile);

    updateUser(fd);

    setTimeout(() => {
      setSave("saved");
      setTimeout(() => setSave(null), 3000);
    }, 900);
  };

  const tabs = [
    { id: "saved", label: "Saved" },
    { id: "notifications", label: "Notifications", badge: unread || null },
    { id: "settings", label: "Settings" },
  ];

  const avStyle = (src) => (src ? { backgroundImage: `url(${src})` } : {});

  return (
    <div className="dashboard-container">
      {/* HERO */}
      <div className="dashboard-hero">
        <div className="dashboard-inner">
          <div className="dashboard-hero-row">
            <div className="dashboard-hero-left">
              <div className="dashboard-avatar-ring">
                <div
                  className="dashboard-avatar"
                  style={avStyle(preview || user?.profile_pic)}
                >
                  {!preview && !user?.profile_pic && (
                    <span className="dashboard-avatar-letter">
                      {(user?.name ?? "U")[0].toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="dashboard-greet">{greeting},</div>
                <div className="dashboard-name">{user?.name ?? "User"}</div>
                <div className="dashboard-email">{user?.email}</div>
              </div>
            </div>

            {isOrganizer && (
              <a className="dashboard-org-btn" href="/organizer-dashboard">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width="14"
                  height="14"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                Organizer Dashboard
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width="12"
                  height="12"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="dashboard-tab-barOuter">
        <div className="dashboard-inner">
          <div className="dashboard-tab-bar">
            {tabs.map((t) => (
              <button
                key={t.id}
                className={`dashboard-tab-btn ${tab === t.id ? "active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {t.badge ? <span className="dashboard-tab-badge">{t.badge}</span> : null}
                {tab === t.id && <span className="dashboard-tab-pip" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="dashboard-inner">
        <div className="dashboard-content" key={tab}>
          {tab === "saved" &&
            (loadingWishlist ? (
              <Empty
                icon="⟳"
                title="Loading saved discounts"
                body="Please wait while we fetch your saved items."
              />
            ) : wishlistError ? (
              <Empty
                icon="⚠"
                title="Unable to load saved discounts"
                body={wishlistError}
              />
            ) : savedItems.length === 0 ? (
              <Empty
                icon="♥"
                title="Nothing saved yet"
                body="Tap the heart on any discount to save it here."
              />
            ) : (
              <div className="dashboard-grid">
                {savedItems.map((d, i) => (
                  <Card
                    key={d.id}
                    discount={d}
                    bgColor="light"
                    index={i}
                    onSave={toggleSave}
                    isSaved={savedIds.has(d.id)}
                  />
                ))}
              </div>
            ))}

          {tab === "notifications" && (
            <>
              <div className="dashboard-notif-filter">
                <select
                  className="dashboard-filter-select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="order_status">Order Status</option>
                  <option value="discount_update">Discount Update</option>
                  <option value="review_posted">Review Posted</option>
                  <option value="ticket_purchased">Ticket Purchased</option>
                </select>
                <div className="dashboard-filter-toggle">
                  <button
                    className={`dashboard-toggle-btn${filterRead === "all" ? " active" : ""}`}
                    onClick={() => setFilterRead("all")}
                  >All</button>
                  <button
                    className={`dashboard-toggle-btn${filterRead === "unread" ? " active" : ""}`}
                    onClick={() => setFilterRead("unread")}
                  >Unread</button>
                  <button
                    className={`dashboard-toggle-btn${filterRead === "read" ? " active" : ""}`}
                    onClick={() => setFilterRead("read")}
                  >Read</button>
                </div>
              </div>
              {!notifications || notifications.results?.length === 0
                ? <Empty icon="🔔" title="All caught up" body="No notifications right now." />
                : <NotificationList
                  notifications={notifications}
                  onMarkRead={markNotificationAsRead}
                  onMarkAllRead={markAllNotificationsAsRead}
                />
              }
            </>
          )}

          {tab === "settings" && (
            <div className="dashboard-settings-grid">
              <div className="dashboard-avatar-col">
                <div
                  className="dashboard-avatar-edit"
                  style={avStyle(preview || user?.profile_pic)}
                  onClick={() => fileRef.current?.click()}
                >
                  {!preview && !user?.profile_pic && (
                    <span
                      style={{
                        fontSize: 34,
                        fontWeight: 700,
                        color: "#fa8128",
                        fontFamily: "'Playfair Display',serif",
                      }}
                    >
                      {(user?.name ?? "U")[0].toUpperCase()}
                    </span>
                  )}
                  <div className="dashboard-avatar-overlay">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      width="16"
                      height="16"
                    >
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                    </svg>
                    Change
                  </div>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFile}
                />
                <div className="dashboard-settings-name">{user?.name ?? "User"}</div>
                <div className="dashboard-settings-email">{user?.email}</div>
              </div>

              <form className="dashboard-settings-form" onSubmit={handleSave}>
                <div>
                  <label className="dashboard-form-label">Full Name</label>
                  <input
                    className="dashboard-form-input"
                    value={uName}
                    onChange={(e) => setUName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="dashboard-form-label">Email</label>
                  <input
                    className={`dashboard-form-input ${emailErr ? "error" : ""}`}
                    type="email"
                    value={uEmail}
                    onChange={(e) => {
                      setUEmail(e.target.value);
                      setEmailErr(
                        e.target.value &&
                          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value)
                          ? "Invalid email"
                          : "",
                      );
                    }}
                    placeholder="you@email.com"
                  />
                  {emailErr && <div className="dashboard-form-error">{emailErr}</div>}
                </div>
                <div>
                  <label className="dashboard-form-label">Phone Number</label>
                  <input
                    className={`dashboard-form-input ${phoneErr ? "error" : ""}`}
                    value={uPhone}
                    onChange={(e) => {
                      setUPhone(e.target.value);
                      setPhoneErr(
                        e.target.value && e.target.value.length < 7
                          ? "Invalid number"
                          : "",
                      );
                    }}
                    placeholder="+233 XX XXX XXXX"
                  />
                  {phoneErr && <div className="dashboard-form-error">{phoneErr}</div>}
                </div>
                <div className="dashboard-form-footer">
                  <button
                    type="submit"
                    className="dashboard-submit-btn"
                    disabled={
                      !!emailErr || !!phoneErr || saveState === "saving"
                    }
                  >
                    {saveState === "saving" ? (
                      <>
                        <span className="dashboard-spinner" /> Saving…
                      </>
                    ) : saveState === "saved" ? (
                      "✓ Saved!"
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.userState.user,
  wishlist: state.discountState.wishlist,
  discounts: state.discountState.discounts,
  notifications: state.userState.notifications,
  organizer: state.organizerState.organizer,
  loading: state.appState.loading,
  token: state.userState.token,
});

const mapDispatchToProps = (dispatch) => ({
  addToWishlist: (data) => dispatch(addToWishlistAPI(data)),
  removeFromWishlist: (data) => dispatch(removeFromWishlistAPI(data)),
  getWishlist: () => dispatch(getWishlistAPI()),
  getDiscounts: () => dispatch(getDiscountsAPI()),
  getUserNotifications: (filters) => dispatch(getUserNotificationsAPI(filters)),
  updateUser: (data) => dispatch(userUpdateAPI(data)),
  markNotificationAsRead: (id) => dispatch(markNotificationAsReadAPI(id)),
  markAllNotificationsAsRead: () => dispatch(markAllNotificationsAsReadAPI()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserDashboard);
